import React, { useEffect, useState } from 'react'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Dialog from 'src/components/Dialog'
import { OrderProductSample_fields, OrderRevise_fields } from 'src/types/globalTypes'
import { useRefresh, useDataProvider, useNotify } from 'react-admin'
import { OrderLineItems_expanded } from '../types'
import order_template2 from '../utils/exportPDF/template2'
import { useFormState } from 'react-final-form'
import order_template3 from '../utils/exportPDF/template3'
import useCreateUserLog from 'src/hooks/useCreateUserLog'

const pdf = pdfMake
pdf.vfs = pdfFonts.pdfMake.vfs

interface Props {
  disabled?: boolean
  revise?: OrderRevise_fields
  lineItems: OrderLineItems_expanded[]
  productSamples: OrderProductSample_fields[]
  current_num: number
}

export default function NewReviseButton(props: Props) {
  const { disabled, revise, current_num, lineItems, productSamples } = props
  const { values } = useFormState()
  const { createLog } = useCreateUserLog()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const refresh = useRefresh()
  const dataProvider = useDataProvider()
  const notify = useNotify()

  let temp2 = order_template2({ data: values as any, lineItems, revise, productSamples }) as any
  let temp3 = order_template3({ data: values as any, lineItems, revise }) as any

  useEffect(() => {
    setText(`REVISE-${current_num + 1}`)
  }, [current_num])

  const handleNewRevise = async () => {
    const orderId = values.id

    if (!orderId) {
      notify('Error: Order ID is missing', { type: 'warning' })
      return
    }

    if (!revise?.id) {
      notify('Error: Revise record is missing', { type: 'warning' })
      return
    }

    if (isCreating) return
    setIsCreating(true)

    try {
      // Fetch fresh order from server to get real current_revise_num (avoid stale cache)
      const { data: freshOrder } = await dataProvider.getOne('order', { id: orderId })
      const serverCurrentNum = freshOrder.current_revise_num ?? current_num
      const nextReviseNum = serverCurrentNum + 1
      const reviseName = text || `REVISE-${nextReviseNum}`
      let step = 0

      try {
        // Step 1: Update existing order_revise (bump revise_key and name)
        step = 1
        await dataProvider.update('order_revise', {
          id: revise.id,
          data: { date: new Date(), name: reviseName, revise_key: nextReviseNum },
          previousData: revise,
        })

        // Step 2: Update line items' revise_key (no duplication — same items, new key)
        step = 2
        const ids = lineItems.map((item) => item.id)
        if (ids.length) {
          await dataProvider.updateMany('order_line_item', { ids, data: { revise_key: nextReviseNum } })
        }

        // Step 3: Update order current_revise_num
        step = 3
        await dataProvider.update('order', {
          id: orderId,
          data: { current_revise_num: nextReviseNum },
          previousData: { id: orderId, current_revise_num: serverCurrentNum },
        })
      } catch (error) {
        // Rollback: revert already-committed steps to prevent mismatch
        if (step >= 2) {
          // Step 2 or 3 failed — revert line items back to old revise_key
          const ids = lineItems.map((item) => item.id)
          if (ids.length) {
            await dataProvider.updateMany('order_line_item', { ids, data: { revise_key: serverCurrentNum } }).catch(() => {})
          }
        }
        if (step >= 1) {
          // Step 1+ failed — revert revise record back to old values
          await dataProvider.update('order_revise', {
            id: revise.id,
            data: { name: revise.name, revise_key: serverCurrentNum },
            previousData: { ...revise, revise_key: nextReviseNum },
          }).catch(() => {})
        }
        throw error
      }

      createLog('order', 'UPDATE', `New revise ${values.ref_pi} - ${nextReviseNum}`)
      pdf.createPdf(temp2).download(`${values.ref_pi}-expenses-revise-${serverCurrentNum}`)
      pdf.createPdf(temp3).download(`${values.ref_pi}-loading-revise-${serverCurrentNum}`)
      setText('')
      setOpen(false)
      refresh()
    } catch (error) {
      notify('Error creating new revise', { type: 'warning' })
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <>
      <Button color="primary" variant="outlined" onClick={() => setOpen(true)} disabled={disabled}>
        New Revise
      </Button>
      <Dialog
        open={open}
        title="New revise"
        onClose={() => setOpen(false)}
        onCancel={() => setOpen(false)}
        onSave={handleNewRevise}
      >
        <TextField label="Title" fullWidth value={text} onChange={(e) => setText(e.target.value)} variant="filled" />
      </Dialog>
    </>
  )
}
